import { holidayDateLabel, holidayModeLabel } from './holiday-labels.js';
import { SchedulingCredit } from './credit-slot.js';
import {
  Button,
  SegmentedControl,
  FloatingPanel,
  formatCurrency,
  MultiSelect,
  Tooltip,
  useIsWideViewport,
  useSnackbar,
  type CivilDate,
} from '@hue/design-system';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  TriangleAlert,
  CalendarOff,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  ClipboardCheck,
  HeartPulse,
  MessageCircle,
  Plus,
  X,
} from 'lucide-react';
import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import {
  getApiError,
  schedulingApi,
  type Appointment,
  type SchedulingResource,
} from '../../lib/api.js';
import { formatDateTime } from '../../lib/time-zone.js';
import { EmptyCard, ErrorCard, LoadingCard } from '../../app/data-view.js';
import { isLive, useAppointmentConfirmation } from './confirmation.js';
import { useOpenChart, useOpenClosing } from './closing-link.js';
import { AppointmentQueueNotice, AppointmentStatusPill } from './shared.js';
import { AppointmentDepositNotice } from './appointment-deposit.js';
import { PortalCancellationCard } from './portal-cancellation-card.js';
import type { SchedulingView } from '../../app/list-views.js';
import { useOperationalPeriod } from '../../app/operational-period-context.js';
import type { ViewSetter } from '../../app/view-state.js';
import {
  AppointmentDialog,
  CancellationDialog,
  type AppointmentSeed,
} from './appointment-dialog.js';
import {
  professionalLabel,
  roomLabel,
  DAY_MINUTES,
  calendarDate,
  complement,
  SLOT_MINUTES,
  WEEKDAY_SHORT,
  civilMoment,
  dayLabel,
  dayOfMonth,
  gridRange,
  groupBlocks,
  layoutLanes,
  minuteLabel,
  monthName,
  monthOf,
  periodLabel,
  periodRange,
  placeAppointments,
  placeBlocks,
  seriesColor,
  shiftDate,
  slotCheck,
  type SlotCheck,
  snap,
  visibleDays,
  weekdayOf,
  windowsOn,
  type CalendarView,
  type PlacedAppointment,
  type PlacedBlock,
} from './calendar-model.js';
import {
  useResourceOptions,
  useServiceOptions,
  type SchedulingCapabilities,
} from './scheduling-options.js';

/// Altura de uma hora na grade. A régua inteira deriva dela.
const HOUR_PX = 56;
/// Faixa livre à direita de cada coluna da grade: clicar nela cria no horário, mesmo sob um
/// agendamento.
const CREATE_GUTTER_PX = 16;
/// Barra à esquerda que marca o bloqueio e abre os detalhes dele. Os cards começam depois dela.
const BLOCK_BAR_PX = 9;
/// Folga em volta de um agendamento onde a prévia de "criar novo" não aparece: é onde o ponteiro
/// vira o de esticar, e ali um clique quer dizer outra coisa.
const EDGE_PX = 6;
/// Distância que o ponteiro precisa andar para um aperto virar arraste.
const DRAG_THRESHOLD_PX = 5;
/// Quanto tempo depois de soltar um clique ainda é o fim do arraste, e não um clique novo.
const DRAG_CLICK_GRACE_MS = 400;
const MAX_MONTH_ITEMS = 3;
/// Até quatro sobreposições ainda ficam legíveis lado a lado. A quinta transforma o trecho numa
/// pasta de agendamentos, em vez de reduzir cada cartão a uma lâmina sem texto.
const MAX_VISIBLE_LANES = 4;
/// Cor de quem não está na lista de profissionais ativos.
const NEUTRAL = 'var(--content-secondary)';

interface DropTarget {
  professionalId: string;
  date: CivilDate;
  start: number;
  end: number;
}

export function CalendarPanel({
  capabilities,
  timeZone,
  today,
  view,
  onView,
}: {
  capabilities: SchedulingCapabilities;
  timeZone: string;
  today: CivilDate;
  view: SchedulingView;
  onView: ViewSetter<SchedulingView>;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const narrow = !useIsWideViewport();
  const { month, setMonth } = useOperationalPeriod(today);
  const date = calendarDate(view.date, month, today);
  /// Aberta sem dia no endereço (pelo menu), a agenda mostra hoje mesmo que a casca esteja noutro
  /// mês; quem navega dentro dela leva o dia na URL e mantém o mês escolhido.
  const [openedWithoutDate] = useState(view.date === null);
  useEffect(() => {
    if (openedWithoutDate && month !== 'ALL' && month !== monthOf(today)) setMonth(monthOf(today));
    // Só na abertura: depois disso, trocar o mês na casca leva o calendário junto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const resources = useResourceOptions();
  const services = useServiceOptions();
  const allResources = resources.data ?? [];
  const professionals = allResources.filter(
    (item) => item.kind === 'PROFESSIONAL' && item.attendsSchedule !== false,
  );
  const rooms = allResources.filter((item) => item.kind === 'ROOM');
  const colors = new Map(professionals.map((item, index) => [item.id, seriesColor(index)]));
  /// Profissional arquivado não está na lista, mas seus agendamentos continuam no calendário.
  const colorOf = (professionalId: string) => colors.get(professionalId) ?? NEUTRAL;
  const range = periodRange(view.view, date, narrow);
  const filters = {
    from: range.from,
    to: range.to,
    includeCancelled: view.cancelled,
    ...(view.professionals ? { professionals: view.professionals } : {}),
    ...(view.rooms
      ? {
          rooms: view.rooms.filter((item) => item !== 'none'),
          withoutRoom: view.rooms.includes('none'),
        }
      : {}),
  };
  const calendar = useQuery({
    queryKey: ['company', 'scheduling', 'calendar', filters],
    queryFn: () => schedulingApi.getCalendar(filters),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const detail = useQuery({
    queryKey: ['company', 'scheduling', 'appointment', view.appointment],
    queryFn: () => schedulingApi.getAppointment(view.appointment),
    enabled: Boolean(view.appointment),
    retry: false,
  });
  const [seed, setSeed] = useState<AppointmentSeed | null>(null);
  const [cancelTarget, setCancelTarget] = useState<Appointment>();
  const [selected, setSelected] = useState<PlacedAppointment | null>(null);
  const anchorRef = useRef<HTMLElement | null>(null);
  const appointments = placeAppointments(calendar.data?.appointments ?? [], timeZone);
  const blocks = placeBlocks(calendar.data?.blocks ?? [], timeZone);
  const holidays = calendar.data?.holidays;
  const visibleProfessionals = view.professionals
    ? professionals.filter((item) => view.professionals!.includes(item.id))
    : professionals;
  const canDrag = capabilities.canUpdate && !narrow;

  /// Todo movimento de dia passa por aqui: cruzar o mês leva o mês de trabalho junto.
  function go(next: CivilDate, patch: { view?: CalendarView } = {}) {
    onView({ date: next, ...patch });
    if (month !== 'ALL' && monthOf(next) !== month) setMonth(monthOf(next));
  }

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ['company', 'scheduling'] });
  }

  function problemOf(appointment: PlacedAppointment, target: DropTarget): SlotCheck {
    return slotCheck(
      { resources: allResources, appointments, blocks, holidays, allowAppointmentOverlap: true },
      {
        appointmentId: appointment.id,
        roomId: appointment.anyRoom ? 'ANY' : appointment.roomId,
        ...target,
      },
    );
  }

  /// Arrastar e esticar remarcam na hora e oferecem o caminho de volta no próprio aviso. Desfazer é
  /// outra remarcação (ADR 0034, decisão 6).
  async function reschedule(
    appointment: PlacedAppointment,
    target: DropTarget,
    kind: 'move' | 'resize',
    warning = '',
  ) {
    const send = (to: DropTarget) =>
      schedulingApi.rescheduleAppointment(appointment.id, {
        professionalId:
          appointment.anyProfessional && to.professionalId === appointment.professionalId
            ? 'ANY'
            : to.professionalId,
        roomId: appointment.anyRoom ? 'ANY' : appointment.roomId,
        date: to.date,
        startTime: minuteLabel(to.start),
        ...(kind === 'resize' ? { durationMinutes: to.end - to.start } : {}),
      });
    const previous: DropTarget = {
      professionalId: appointment.professionalId,
      date: appointment.date,
      start: appointment.start,
      end: appointment.end,
    };
    const professional = professionals.find((item) => item.id === target.professionalId);
    try {
      await send(target);
      await refresh();
      // Fora do expediente é exceção legítima e passa; o aviso só lembra que saiu do expediente.
      const overtime = warning
        ? ` Ficou ${warning === 'não atende neste dia' ? 'num dia sem expediente' : warning}.`
        : '';
      snackbar.success(
        (kind === 'resize'
          ? `Agendamento de ${appointment.clientDisplayName} agora dura ${target.end - target.start} min.`
          : `Agendamento de ${appointment.clientDisplayName} remarcado para ${shortDate(target.date)} às ${minuteLabel(target.start)}${
              target.professionalId === appointment.professionalId || !professional
                ? ''
                : ` com ${professional.displayName}`
            }.`) + overtime,
        {
          label: 'Desfazer',
          onAction: () =>
            void send(previous)
              .then(refresh)
              .then(() => snackbar.show('Alteração desfeita.', 'info'))
              .catch((cause: unknown) => snackbar.failure(getApiError(cause))),
        },
      );
    } catch (cause) {
      snackbar.failure(getApiError(cause));
    }
  }

  function refuse(appointment: PlacedAppointment, reason: string) {
    snackbar.show(
      `Não coube: ${reason}. O agendamento de ${appointment.clientDisplayName} continua às ${minuteLabel(appointment.start)}.`,
      'info',
    );
  }

  function openDetail(appointment: PlacedAppointment, element: HTMLElement) {
    anchorRef.current = element;
    setSelected(appointment);
  }
  const closeDetail = useCallback(() => setSelected(null), [setSelected]);

  const create = capabilities.canCreate ? (next: AppointmentSeed) => setSeed(next) : undefined;
  const loading = resources.isLoading || (calendar.isLoading && !calendar.data);
  const failure = resources.error ?? calendar.error;

  return (
    <div role="tabpanel" aria-label="Calendário">
      <section className="overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
        <div className="flex flex-wrap items-center gap-3 border-b border-surface-border px-4 py-3">
          <Button variant="outline" size="sm" onClick={() => go(today)}>
            Hoje
          </Button>
          <div className="flex gap-1">
            <NavButton
              label="Período anterior"
              onClick={() => go(shiftDate(view.view, date, -1, narrow))}
            >
              <ChevronLeft className="size-4" />
            </NavButton>
            <NavButton
              label="Próximo período"
              onClick={() => go(shiftDate(view.view, date, 1, narrow))}
            >
              <ChevronRight className="size-4" />
            </NavButton>
          </div>
          <h2
            aria-live="polite"
            className="min-w-0 flex-1 basis-48 text-base font-semibold tabular-nums"
          >
            {periodLabel(view.view, date, narrow)}
          </h2>
          <SegmentedControl
            compact
            label="Visão do calendário"
            value={view.view}
            onChange={(view) => onView({ view })}
            options={[
              { value: 'month', label: 'Mês' },
              { value: 'week', label: 'Semana' },
              { value: 'day', label: 'Dia' },
            ]}
          />
          {create ? (
            <Button size="sm" onClick={() => create({ date })}>
              <Plus className="size-4" /> Novo agendamento
            </Button>
          ) : null}
        </div>
        <CalendarFilters
          professionals={professionals}
          rooms={rooms}
          colorOf={colorOf}
          view={view}
          onView={onView}
        />
        {holidays?.length ? (
          <ul aria-label="Feriados no período" className="border-b border-surface-border px-4 py-2">
            {/* Uma linha por data: com várias unidades, cada uma pode ter decidido diferente. */}
            {[
              ...new Map(
                holidays.map((holiday) => [`${holiday.date}:${holiday.unitId}`, holiday]),
              ).values(),
            ]
              .sort((left, right) => left.date.localeCompare(right.date))
              .map((holiday) => (
                <li
                  key={`${holiday.date}:${holiday.unitId}`}
                  className="flex items-center gap-2 py-0.5 text-xs font-medium text-warning"
                >
                  <CalendarOff className="size-3.5 shrink-0" />
                  {holidayDateLabel(holiday.date)} · {holiday.name} · {holidayModeLabel(holiday)}
                </li>
              ))}
          </ul>
        ) : null}
        {failure ? (
          <div className="p-4">
            <ErrorCard message={getApiError(failure)} />
          </div>
        ) : loading ? (
          <div className="p-4">
            <LoadingCard label="Carregando calendário" />
          </div>
        ) : view.view === 'month' ? (
          <MonthView
            date={date}
            today={today}
            narrow={narrow}
            appointments={appointments}
            colorOf={colorOf}
            canDrag={canDrag}
            onCreate={create}
            onOpenDay={(day) => go(day, { view: 'day' })}
            onSelectDay={(day) => go(day)}
            onOpen={openDetail}
            validate={problemOf}
            onDrop={(appointment, target, warning) =>
              void reschedule(appointment, target, 'move', warning)
            }
            onRefuse={refuse}
          />
        ) : view.view === 'week' ? (
          <TimeGrid
            key={`week-${range.from}`}
            columns={visibleDays('week', date, narrow).map((day) => {
              const working = visibleProfessionals.flatMap((item) =>
                windowsOn(item, day, holidays),
              );
              return {
                key: day,
                date: day,
                label: (
                  <button
                    type="button"
                    onClick={() => go(day, { view: 'day' })}
                    aria-label={`Abrir ${dayLabel(day)}`}
                    className="inline-flex cursor-pointer flex-col items-center gap-0.5 rounded-xl px-2 py-1 transition hover:bg-surface-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
                  >
                    <span className="text-[11px] font-bold uppercase tracking-widest text-content-muted">
                      {WEEKDAY_SHORT[weekdayOf(day)]}
                    </span>
                    <span
                      className={`grid size-9 place-items-center rounded-full text-lg font-semibold tabular-nums ${
                        day === today ? 'bg-brand-500 text-on-brand' : ''
                      }`}
                    >
                      {dayOfMonth(day)}
                    </span>
                  </button>
                ),
                ariaLabel: dayLabel(day),
                working:
                  working.length === 0
                    ? []
                    : [
                        {
                          start: Math.min(...working.map((item) => item.start)),
                          end: Math.max(...working.map((item) => item.end)),
                        },
                      ],
                items: appointments.filter((item) => item.date === day),
                blocks: blocks.filter((item) => item.date === day),
              };
            })}
            resources={allResources}
            today={today}
            timeZone={timeZone}
            colorOf={colorOf}
            detailed={false}
            canDrag={canDrag}
            onCreate={create}
            onOpen={openDetail}
            validate={problemOf}
            onDrop={(appointment, target, kind, warning) =>
              void reschedule(appointment, target, kind, warning)
            }
            onRefuse={refuse}
          />
        ) : visibleProfessionals.length === 0 ? (
          <div className="p-4">
            <EmptyCard
              icon={Plus}
              title="Nenhum profissional à vista"
              description="Escolha ao menos um profissional nos filtros para ver o dia."
            />
          </div>
        ) : (
          <TimeGrid
            key={`day-${date}`}
            minColumnWidth={narrow ? 112 : 140}
            columns={visibleProfessionals.map((professional) => {
              const windows = windowsOn(professional, date, holidays);
              const items = appointments.filter(
                (item) => item.date === date && item.professionalId === professional.id,
              );
              const active = items.filter((item) => item.status !== 'CANCELLED').length;
              return {
                key: professional.id,
                date,
                professionalId: professional.id,
                label: (
                  <Tooltip content={professional.displayName} side="bottom">
                    <span className="-mx-1 -my-1.5 flex w-[calc(100%+0.5rem)] min-w-0 cursor-default flex-col items-center gap-0.5 px-2 py-2.5">
                      <span className="flex max-w-full items-center gap-1.5 text-sm font-semibold">
                        <span
                          aria-hidden="true"
                          className="size-2 shrink-0 rounded-full"
                          style={{ backgroundColor: `rgb(${colorOf(professional.id)})` }}
                        />
                        <span className="truncate">{professional.displayName}</span>
                      </span>
                      <span className="text-[11px] tabular-nums text-content-muted">
                        {windows.length === 0
                          ? 'Não atende neste dia'
                          : `${windows.map((item) => `${minuteLabel(item.start)}–${minuteLabel(item.end)}`).join(', ')} · ${active} agend.`}
                      </span>
                    </span>
                  </Tooltip>
                ),
                ariaLabel: professional.displayName,
                working: windows,
                items,
                blocks: blocks.filter(
                  (item) => item.date === date && item.resourceId === professional.id,
                ),
              };
            })}
            resources={allResources}
            today={today}
            timeZone={timeZone}
            colorOf={colorOf}
            detailed
            canDrag={canDrag}
            onCreate={create}
            onOpen={openDetail}
            validate={problemOf}
            onDrop={(appointment, target, kind, warning) =>
              void reschedule(appointment, target, kind, warning)
            }
            onRefuse={refuse}
          />
        )}
        <Legend />
      </section>

      {selected ? (
        <FloatingPanel
          anchorRef={anchorRef}
          onDismiss={closeDetail}
          role="dialog"
          aria-label={`Agendamento de ${selected.clientDisplayName}`}
          className="w-80 max-w-[calc(100vw-2rem)] rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card"
        >
          <div onPointerDown={(event) => event.stopPropagation()}>
            <AppointmentDetail
              appointment={selected}
              capabilities={capabilities}
              timeZone={timeZone}
              onChanged={(changed) =>
                setSelected((current) =>
                  current?.id === changed.id
                    ? {
                        ...current,
                        status: changed.status,
                        confirmedAt: changed.confirmedAt,
                      }
                    : current,
                )
              }
              onClose={closeDetail}
              onReschedule={() => {
                closeDetail();
                onView({ appointment: selected.id });
              }}
              onCancel={() => {
                closeDetail();
                setCancelTarget(selected);
              }}
            />
          </div>
        </FloatingPanel>
      ) : null}

      <AppointmentDialog
        open={seed !== null}
        onOpenChange={() => setSeed(null)}
        appointment={null}
        seed={seed ?? undefined}
        today={today}
        timeZone={timeZone}
        services={services.data?.services ?? []}
        professionals={professionals}
        rooms={rooms}
        onSaved={async () => {
          await refresh();
          setSeed(null);
          snackbar.success('Agendamento criado.');
        }}
      />
      <AppointmentDialog
        open={Boolean(view.appointment)}
        onOpenChange={() => onView({ appointment: '' })}
        appointment={detail.data ?? null}
        loading={detail.isLoading}
        error={detail.error ? getApiError(detail.error) : undefined}
        today={today}
        timeZone={timeZone}
        services={services.data?.services ?? []}
        professionals={professionals}
        rooms={rooms}
        readOnly={!capabilities.canUpdate}
        onSaved={async () => {
          await refresh();
          onView({ appointment: '' });
          snackbar.success('Agendamento remarcado.');
        }}
      />
      {cancelTarget ? (
        <CancellationDialog
          appointment={cancelTarget}
          onClose={() => setCancelTarget(undefined)}
          onSaved={async () => {
            await refresh();
            setCancelTarget(undefined);
            snackbar.success('Agendamento cancelado.');
          }}
        />
      ) : null}
    </div>
  );
}

function shortDate(date: CivilDate): string {
  return `${WEEKDAY_SHORT[weekdayOf(date)]!.toLowerCase()}, ${date.slice(8, 10)}/${date.slice(5, 7)}`;
}

function NavButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="grid size-9 cursor-pointer place-items-center rounded-xl border border-surface-border text-content-muted transition hover:border-brand-500/60 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
    >
      {children}
    </button>
  );
}

function CalendarFilters({
  professionals,
  rooms,
  colorOf,
  view,
  onView,
}: {
  professionals: SchedulingResource[];
  rooms: SchedulingResource[];
  colorOf: (professionalId: string) => string;
  view: SchedulingView;
  onView: ViewSetter<SchedulingView>;
}) {
  const allProfessionals = view.professionals === undefined;
  const allRooms = view.rooms === undefined;
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-3 border-b border-surface-border px-4 py-3">
      <FilterRow label="Profissionais">
        <FilterChips
          label="Profissionais"
          itemNoun="profissionais"
          options={professionals.map((item) => ({
            value: item.id,
            label: item.displayName,
            color: colorOf(item.id),
          }))}
          selected={view.professionals}
          onChange={(professionals) => onView({ professionals })}
        />
        <LinkButton onClick={() => onView({ professionals: allProfessionals ? [] : undefined })}>
          {allProfessionals ? 'Limpar' : 'Todos'}
        </LinkButton>
      </FilterRow>
      <FilterRow label="Salas">
        <FilterChips
          label="Salas"
          itemNoun="salas"
          options={[
            ...rooms.map((item) => ({ value: item.id, label: item.displayName })),
            { value: 'none', label: 'Sem sala' },
          ].map((item) => ({ ...item, color: 'var(--content-secondary)' }))}
          selected={view.rooms}
          onChange={(rooms) => onView({ rooms })}
        />
        <LinkButton onClick={() => onView({ rooms: allRooms ? [] : undefined })}>
          {allRooms ? 'Limpar' : 'Todas'}
        </LinkButton>
        <button
          type="button"
          role="switch"
          aria-checked={view.cancelled}
          onClick={() => onView({ cancelled: !view.cancelled })}
          className="ml-auto inline-flex min-h-8 cursor-pointer items-center gap-2 rounded-lg px-1 text-xs text-content-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
        >
          <span
            aria-hidden="true"
            className={`relative h-[18px] w-[30px] rounded-full transition ${view.cancelled ? 'bg-brand-500' : 'bg-surface-border'}`}
          >
            <span
              className={`absolute top-0.5 size-3.5 rounded-full transition ${
                view.cancelled ? 'left-[14px] bg-on-brand' : 'left-0.5 bg-content'
              }`}
            />
          </span>
          Mostrar cancelados
        </button>
      </FilterRow>
    </div>
  );
}

function FilterRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div
      role="group"
      aria-label={label}
      className="flex w-full flex-wrap items-center gap-2 sm:w-auto"
    >
      <span
        aria-hidden="true"
        className="label-overline w-full shrink-0 text-content-muted sm:w-auto"
      >
        {label}
      </span>
      {children}
    </div>
  );
}

/// Seleção múltipla compacta com busca. `undefined` é "todos", como na URL.
function FilterChips({
  label,
  itemNoun,
  options,
  selected,
  onChange,
}: {
  label: string;
  itemNoun: string;
  options: Array<{ value: string; label: string; color: string }>;
  selected: string[] | undefined;
  onChange: (values: string[] | undefined) => void;
}) {
  const all = options.map((option) => option.value);
  return (
    <MultiSelect
      label={label}
      hideLabel
      className="min-w-0 flex-[1_1_10rem] sm:w-64 sm:flex-none"
      itemNoun={itemNoun}
      options={options}
      selected={selected ?? all}
      onChange={(values) => {
        const next = all.filter((value) => values.includes(value));
        onChange(next.length === all.length ? undefined : next);
      }}
    />
  );
}

function LinkButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="min-h-8 cursor-pointer rounded-lg px-2 text-xs font-semibold text-brand-400 transition hover:bg-brand-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
    >
      {children}
    </button>
  );
}

function eventStyle(color: string, cancelled: boolean): CSSProperties {
  return cancelled
    ? { borderColor: `rgb(${color} / 0.55)` }
    : {
        borderColor: `rgb(${color} / 0.55)`,
        backgroundImage: `linear-gradient(rgb(${color} / 0.18), rgb(${color} / 0.18))`,
      };
}

const HATCH: CSSProperties = {
  backgroundImage:
    'repeating-linear-gradient(135deg, rgb(var(--content-faint) / 0.16) 0 6px, transparent 6px 12px)',
};

/// Um aperto com o botão principal do mouse ou da caneta vira arraste depois de alguns pixels. No
/// toque não: o gesto é a rolagem da grade (ADR 0034, decisão 7). Esc devolve tudo ao lugar.
function usePointerDrag() {
  const endedAt = useRef(-Infinity);
  const [pressing, setPressing] = useState(false);
  const begin = useCallback(
    (
      event: ReactPointerEvent,
      handlers: { onMove: (move: PointerEvent) => void; onEnd: (aborted: boolean) => void },
    ) => {
      if (event.button !== 0 || event.pointerType === 'touch') return false;
      event.stopPropagation();
      const origin = { x: event.clientX, y: event.clientY };
      let started = false;
      setPressing(true);
      const finish = (aborted: boolean) => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('keydown', key, true);
        setPressing(false);
        // Só um arraste de fato engole o clique que vem junto; aperto parado é clique comum.
        if (!started) return;
        endedAt.current = performance.now();
        handlers.onEnd(aborted);
      };
      const move = (next: PointerEvent) => {
        if (!started) {
          if (Math.hypot(next.clientX - origin.x, next.clientY - origin.y) < DRAG_THRESHOLD_PX) {
            return;
          }
          started = true;
        }
        handlers.onMove(next);
      };
      const up = () => finish(false);
      const key = (next: KeyboardEvent) => {
        if (next.key !== 'Escape') return;
        next.stopPropagation();
        finish(true);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('keydown', key, true);
      return true;
    },
    [],
  );
  const justDragged = () => performance.now() - endedAt.current < DRAG_CLICK_GRACE_MS;
  return { begin, pressing, justDragged };
}

function MonthView({
  date,
  today,
  narrow,
  appointments,
  colorOf,
  canDrag,
  onCreate,
  onOpenDay,
  onSelectDay,
  onOpen,
  validate,
  onDrop,
  onRefuse,
}: {
  date: CivilDate;
  today: CivilDate;
  narrow: boolean;
  appointments: PlacedAppointment[];
  colorOf: (professionalId: string) => string;
  canDrag: boolean;
  onCreate: ((seed: AppointmentSeed) => void) | undefined;
  onOpenDay: (day: CivilDate) => void;
  onSelectDay: (day: CivilDate) => void;
  onOpen: (appointment: PlacedAppointment, element: HTMLElement) => void;
  validate: (appointment: PlacedAppointment, target: DropTarget) => SlotCheck;
  onDrop: (appointment: PlacedAppointment, target: DropTarget, warning: string) => void;
  onRefuse: (appointment: PlacedAppointment, reason: string) => void;
}) {
  const drag = usePointerDrag();
  const [dragging, setDragging] = useState<{
    id: string;
    day: CivilDate | null;
    reason: string;
  } | null>(null);
  const days = visibleDays('month', date, narrow);
  const byDay = (day: CivilDate) =>
    appointments.filter((item) => item.date === day).sort((a, b) => a.start - b.start);
  const selectedList = byDay(date);

  function startDrag(event: ReactPointerEvent, appointment: PlacedAppointment) {
    if (!canDrag || !isLive(appointment.status)) return;
    let target: { day: CivilDate; check: SlotCheck } | null = null;
    drag.begin(event, {
      onMove: (move) => {
        const cell = document
          .elementFromPoint(move.clientX, move.clientY)
          ?.closest<HTMLElement>('[data-calendar-day]');
        const day = cell?.dataset.calendarDay ?? null;
        target = day
          ? {
              day,
              check: validate(appointment, {
                professionalId: appointment.professionalId,
                date: day,
                start: appointment.start,
                end: appointment.end,
              }),
            }
          : null;
        setDragging({ id: appointment.id, day, reason: target?.check.problem ?? '' });
      },
      onEnd: (aborted) => {
        setDragging(null);
        if (aborted || !target || target.day === appointment.date) return;
        if (target.check.problem) return onRefuse(appointment, target.check.problem);
        onDrop(
          appointment,
          {
            professionalId: appointment.professionalId,
            date: target.day,
            start: appointment.start,
            end: appointment.end,
          },
          target.check.warning,
        );
      },
    });
  }

  return (
    <div>
      <div role="grid" aria-label={periodLabel('month', date, narrow)} className="grid grid-cols-7">
        <div role="row" className="contents">
          {[1, 2, 3, 4, 5, 6, 0].map((weekday) => (
            <div
              key={weekday}
              role="columnheader"
              className="border-b border-surface-border px-1 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-content-muted sm:px-2.5 sm:text-left sm:text-[11px]"
            >
              {WEEKDAY_SHORT[weekday]}
            </div>
          ))}
        </div>
        <div role="row" className="contents">
          {days.map((day, index) => {
            const list = byDay(day);
            const outside = monthOf(day) !== monthOf(date);
            const shown = list.length > MAX_MONTH_ITEMS + 1 ? list.slice(0, MAX_MONTH_ITEMS) : list;
            const isTarget = dragging?.day === day;
            return (
              <div
                key={day}
                role="gridcell"
                data-calendar-day={day}
                aria-label={`${dayOfMonth(day)} de ${monthName(day)}, ${list.length} ${list.length === 1 ? 'agendamento' : 'agendamentos'}`}
                onClick={() => {
                  if (drag.justDragged()) return;
                  if (narrow) onSelectDay(day);
                  else onCreate?.({ date: day });
                }}
                className={`group relative flex min-h-[58px] min-w-0 cursor-pointer flex-col gap-0.5 border-b border-surface-border p-1 transition sm:min-h-[118px] sm:p-1.5 ${
                  (index + 1) % 7 === 0 ? '' : 'border-r'
                } ${outside ? 'bg-surface/50' : ''} ${
                  isTarget
                    ? dragging.reason
                      ? 'bg-danger/10 ring-2 ring-inset ring-danger'
                      : 'bg-brand-500/10 ring-2 ring-inset ring-brand-500'
                    : narrow && day === date
                      ? 'ring-2 ring-inset ring-brand-500/70'
                      : 'hover:bg-brand-500/5'
                }`}
              >
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpenDay(day);
                    }}
                    aria-label={`Abrir ${dayLabel(day)}`}
                    className={`grid size-7 cursor-pointer place-items-center rounded-full text-xs font-semibold tabular-nums transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 ${
                      day === today
                        ? 'bg-brand-500 text-on-brand'
                        : outside
                          ? 'text-content-faint hover:bg-surface-elevated'
                          : 'hover:bg-surface-elevated'
                    }`}
                  >
                    {dayOfMonth(day)}
                  </button>
                  {onCreate && !narrow ? (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        onCreate({ date: day });
                      }}
                      aria-label={`Agendar em ${dayOfMonth(day)} de ${monthName(day)}`}
                      className="cursor-pointer rounded-md px-1 text-[11px] font-semibold text-brand-400 opacity-0 transition group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
                    >
                      + agendar
                    </button>
                  ) : null}
                </div>
                {narrow ? (
                  <div aria-hidden="true" className="flex flex-wrap justify-center gap-0.5">
                    {list.slice(0, 6).map((item) => (
                      <span
                        key={item.id}
                        className="size-1.5 rounded-full"
                        style={{
                          backgroundColor: `rgb(${colorOf(item.professionalId)})`,
                        }}
                      />
                    ))}
                  </div>
                ) : (
                  <>
                    {shown.map((item) => (
                      <Tooltip
                        key={item.id}
                        content={item.overlapWarning ?? item.clientDisplayName}
                      >
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            if (!drag.justDragged()) onOpen(item, event.currentTarget);
                          }}
                          onPointerDown={(event) => startDrag(event, item)}
                          aria-label={`${item.clientDisplayName}, ${minuteLabel(item.start)}, ${item.serviceDisplayName}${item.overlapWarning ? ', ' + item.overlapWarning : ''}`}
                          className={`flex w-full min-w-0 cursor-pointer select-none items-center gap-1.5 rounded-md px-1.5 py-0.5 text-left text-[11.5px] transition hover:bg-content/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 ${
                            item.status === 'CANCELLED' ? 'line-through opacity-55' : ''
                          } ${dragging?.id === item.id ? 'pointer-events-none opacity-30' : ''}`}
                        >
                          <span
                            aria-hidden="true"
                            className="size-[7px] shrink-0 rounded-full"
                            style={{
                              backgroundColor: `rgb(${colorOf(item.professionalId)})`,
                            }}
                          />
                          <span className="tabular-nums text-content-secondary">
                            {minuteLabel(item.start)}
                          </span>
                          <span className="truncate">{item.clientDisplayName}</span>
                          {item.overlapWarning ? (
                            <TriangleAlert
                              aria-hidden="true"
                              className="size-3 shrink-0 text-warning"
                            />
                          ) : null}
                          {item.status === 'CONFIRMED' ? (
                            <CircleCheck
                              aria-label="confirmado"
                              className="size-3 shrink-0 text-positive"
                            />
                          ) : item.status === 'COMPLETED' ? (
                            <Check
                              aria-label="concluído"
                              className="size-3 shrink-0 text-positive"
                            />
                          ) : null}
                        </button>
                      </Tooltip>
                    ))}
                    {list.length > shown.length ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpenDay(day);
                        }}
                        className="cursor-pointer rounded-md px-1.5 py-0.5 text-left text-[11.5px] font-semibold text-content-muted transition hover:bg-surface-elevated hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
                      >
                        +{list.length - shown.length} mais
                      </button>
                    ) : null}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {narrow ? (
        <div className="border-t border-surface-border px-4 py-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold">{dayLabel(date)}</h3>
            {onCreate ? (
              <Button size="sm" onClick={() => onCreate({ date })}>
                <Plus className="size-4" /> Agendar
              </Button>
            ) : null}
          </div>
          {selectedList.length === 0 ? (
            <p className="text-xs text-content-muted">Nenhum agendamento neste dia.</p>
          ) : (
            <ul className="divide-y divide-surface-border">
              {selectedList.map((item) => (
                <li key={item.id}>
                  <Tooltip content={item.overlapWarning ?? item.clientDisplayName}>
                    <button
                      type="button"
                      onClick={(event) => onOpen(item, event.currentTarget)}
                      className="grid w-full cursor-pointer grid-cols-[52px_1fr] gap-2 px-1 py-2.5 text-left transition hover:bg-surface-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
                    >
                      <span className="text-sm font-semibold tabular-nums">
                        {minuteLabel(item.start)}
                      </span>
                      <span className="min-w-0 text-sm">
                        <span className="flex items-center gap-1">
                          {item.overlapWarning ? (
                            <TriangleAlert
                              aria-hidden="true"
                              className="size-3 shrink-0 text-warning"
                            />
                          ) : null}
                          <span className="truncate">{item.clientDisplayName}</span>
                        </span>
                        {item.overlapWarning ? (
                          <span className="sr-only">{item.overlapWarning}</span>
                        ) : null}
                        <span className="block truncate text-xs text-content-muted">
                          {item.serviceDisplayName} · {professionalLabel(item)}
                          {item.status === 'CANCELLED' ? ' · cancelado' : ''}
                          {item.status === 'CONFIRMED' ? ' · confirmado' : ''}
                          {item.status === 'COMPLETED' ? ' · concluído' : ''}
                        </span>
                      </span>
                    </button>
                  </Tooltip>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

interface GridColumn {
  key: string;
  date: CivilDate;
  professionalId?: string | undefined;
  label: ReactNode;
  ariaLabel: string;
  /// Quando se atende; o resto da faixa fica esmaecido.
  working: Array<{ start: number; end: number }>;
  items: PlacedAppointment[];
  blocks: PlacedBlock[];
}

function TimeGrid({
  columns,
  minColumnWidth,
  resources,
  today,
  timeZone,
  colorOf,
  detailed,
  canDrag,
  onCreate,
  onOpen,
  validate,
  onDrop,
  onRefuse,
}: {
  columns: GridColumn[];
  minColumnWidth?: number | undefined;
  resources: SchedulingResource[];
  today: CivilDate;
  timeZone: string;
  colorOf: (professionalId: string) => string;
  detailed: boolean;
  canDrag: boolean;
  onCreate: ((seed: AppointmentSeed) => void) | undefined;
  onOpen: (appointment: PlacedAppointment, element: HTMLElement) => void;
  validate: (appointment: PlacedAppointment, target: DropTarget) => SlotCheck;
  onDrop: (
    appointment: PlacedAppointment,
    target: DropTarget,
    kind: 'move' | 'resize',
    warning: string,
  ) => void;
  onRefuse: (appointment: PlacedAppointment, reason: string) => void;
}) {
  const drag = usePointerDrag();
  const scrollRef = useRef<HTMLDivElement>(null);
  const groupAnchorRef = useRef<HTMLButtonElement>(null);
  const [openGroup, setOpenGroup] = useState<{
    key: string;
    items: PlacedAppointment[];
  } | null>(null);
  const [ghost, setGhost] = useState<{
    column: string;
    minute: number;
    height: number;
  } | null>(null);
  const [dragging, setDragging] = useState<{
    id: string;
    mode: 'move' | 'resize';
    column: string | null;
    target: DropTarget | null;
    reason: string;
    warning: string;
  } | null>(null);
  const range = gridRange(
    resources,
    columns.flatMap((column) => column.items),
  );
  const top = (minute: number) => ((minute - range.start) / 60) * HOUR_PX;
  const height = ((range.end - range.start) / 60) * HOUR_PX;
  const now = civilMoment(new Date().toISOString(), timeZone);
  const template = `56px repeat(${columns.length}, minmax(${minColumnWidth ?? 0}px, 1fr))`;

  // Abre na manhã, não à meia-noite: o que interessa é o começo do expediente.
  useEffect(() => {
    scrollRef.current!.scrollTop = Math.max(0, top(8 * 60 - 30));
    // Só ao montar: rolar de volta a cada dado novo tiraria a pessoa de onde ela estava.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function minuteAt(element: HTMLElement, clientY: number, offset = 0): number {
    const rect = element.getBoundingClientRect();
    return range.start + ((clientY - rect.top - offset) / HOUR_PX) * 60;
  }

  function hover(event: React.MouseEvent<HTMLDivElement>, column: GridColumn) {
    if (drag.pressing || !onCreate) return setGhost(null);
    const element = event.currentTarget;
    const rect = element.getBoundingClientRect();
    const y = event.clientY - rect.top;
    const slot = Math.min(
      Math.max(
        Math.floor(minuteAt(element, event.clientY) / SLOT_MINUTES) * SLOT_MINUTES,
        range.start,
      ),
      range.end - SLOT_MINUTES,
    );
    // Só em espaço livre: nunca sobre um agendamento, nem perto da borda de esticar, e cortada
    // antes do próximo agendamento da mesma faixa.
    const under = [...element.querySelectorAll<HTMLElement>('[data-calendar-event]')]
      .map((node) => node.getBoundingClientRect())
      .filter((box) => event.clientX >= box.left && event.clientX <= box.right)
      .map((box) => ({ top: box.top - rect.top, bottom: box.bottom - rect.top }));
    if (under.some((box) => y >= box.top - EDGE_PX && y <= box.bottom + EDGE_PX)) {
      return setGhost(null);
    }
    // Na coluna de um profissional, o bloqueio é dele: criar ali seria recusado.
    if (
      column.professionalId &&
      column.blocks.some((block) => slot < block.end && slot + SLOT_MINUTES > block.start)
    ) {
      return setGhost(null);
    }
    const slotTop = top(slot);
    if (under.some((box) => slotTop < box.bottom && slotTop + 4 > box.top)) return setGhost(null);
    const next = Math.min(
      slotTop + HOUR_PX / 2,
      ...under.filter((box) => box.top >= slotTop).map((box) => box.top - 2),
    );
    setGhost({ column: column.key, minute: slot, height: Math.max(next - slotTop, 14) });
  }

  function startDrag(event: ReactPointerEvent<HTMLElement>, appointment: PlacedAppointment) {
    if (!canDrag || !isLive(appointment.status)) return;
    const mode = (event.target as HTMLElement).dataset.calendarGrip ? 'resize' : 'move';
    // O agendamento mora sempre dentro de uma coluna; é nela que o esticar acontece.
    const home = event.currentTarget.closest<HTMLElement>('[data-calendar-column]')!;
    const grab = event.clientY - home.getBoundingClientRect().top - top(appointment.start);
    const length = appointment.end - appointment.start;
    let target: DropTarget | null = null;
    let check: SlotCheck = { problem: '', warning: '' };
    const started = drag.begin(event, {
      onMove: (move) => {
        const column =
          mode === 'move'
            ? document
                .elementFromPoint(move.clientX, move.clientY)
                ?.closest<HTMLElement>('[data-calendar-column]')
            : home;
        if (!column) {
          target = null;
          setDragging({
            id: appointment.id,
            mode,
            column: null,
            target: null,
            reason: '',
            warning: '',
          });
          return;
        }
        let start = appointment.start;
        let end = appointment.end;
        if (mode === 'move') {
          start = Math.min(
            Math.max(snap(minuteAt(column, move.clientY, grab)), range.start),
            range.end - length,
          );
          end = start + length;
        } else {
          end = Math.min(
            Math.max(snap(minuteAt(column, move.clientY)), appointment.start + SLOT_MINUTES),
            Math.min(range.end, DAY_MINUTES),
          );
        }
        target = {
          professionalId: column.dataset.calendarProfessional ?? appointment.professionalId,
          date: column.dataset.calendarColumnDate as CivilDate,
          start,
          end,
        };
        check = validate(appointment, target);
        setDragging({
          id: appointment.id,
          mode,
          column: column.dataset.calendarColumn!,
          target,
          reason: check.problem,
          warning: check.warning,
        });
      },
      onEnd: (aborted) => {
        setDragging(null);
        if (aborted || !target) return;
        const changed =
          target.professionalId !== appointment.professionalId ||
          target.date !== appointment.date ||
          target.start !== appointment.start ||
          target.end !== appointment.end;
        if (!changed) return;
        if (check.problem) return onRefuse(appointment, check.problem);
        onDrop(appointment, target, mode, check.warning);
      },
    });
    if (started) setGhost(null);
  }

  return (
    <div
      ref={scrollRef}
      className="max-h-[640px] overflow-auto overscroll-contain"
      data-calendar-scroll
    >
      <div
        className="relative"
        style={
          minColumnWidth ? { minWidth: `${56 + columns.length * minColumnWidth}px` } : undefined
        }
      >
        <div
          className="sticky top-0 z-20 grid border-b border-surface-border bg-surface-card"
          style={{ gridTemplateColumns: template }}
        >
          <div className="sticky left-0 z-10 bg-surface-card" />
          {columns.map((column) => (
            <div
              key={column.key}
              className="flex min-w-0 justify-center border-l border-surface-border px-1 py-1.5"
            >
              {column.label}
            </div>
          ))}
        </div>
        <div className="relative grid" style={{ gridTemplateColumns: template, height }}>
          <div className="sticky left-0 z-10 bg-surface-card" aria-hidden="true">
            {Array.from({ length: (range.end - range.start) / 60 - 1 }, (_item, index) => {
              const minute = range.start + (index + 1) * 60;
              return (
                <span
                  key={minute}
                  className="absolute right-2 -translate-y-1/2 text-[11px] tabular-nums text-content-muted"
                  style={{ top: top(minute) }}
                >
                  {minuteLabel(minute)}
                </span>
              );
            })}
          </div>
          {columns.map((column) => {
            const lanes = layoutLanes(column.items);
            const denseGroups = appointmentGroups(column.items).filter(
              (group) =>
                Math.max(...group.items.map((item) => lanes.get(item.id)!.lanes)) >
                MAX_VISIBLE_LANES,
            );
            const groupedIds = new Set(
              denseGroups.flatMap((group) => group.items.map((item) => item.id)),
            );
            // Com bloqueio na coluna, os cards abrem espaço para a barra dele à esquerda.
            const inset = column.blocks.length > 0 ? BLOCK_BAR_PX : 0;
            const off = complement(column.working, range);
            const dropHere =
              dragging?.column === column.key && dragging.target ? dragging.target : null;
            return (
              <div
                key={column.key}
                role="group"
                aria-label={column.ariaLabel}
                data-calendar-column={column.key}
                data-calendar-column-date={column.date}
                {...(column.professionalId
                  ? { 'data-calendar-professional': column.professionalId }
                  : {})}
                onMouseMove={(event) => hover(event, column)}
                onMouseLeave={() => setGhost(null)}
                onClick={() => {
                  if (!onCreate || drag.justDragged() || ghost?.column !== column.key) return;
                  onCreate({
                    date: column.date,
                    startTime: minuteLabel(ghost.minute),
                    ...(column.professionalId ? { professionalId: column.professionalId } : {}),
                  });
                }}
                className={`relative min-w-0 border-l border-surface-border ${onCreate ? 'cursor-pointer' : ''} ${
                  column.date === today ? 'bg-brand-500/[0.08]' : ''
                }`}
                style={{
                  backgroundImage:
                    'linear-gradient(rgb(var(--surface-border)) 1px, transparent 1px), linear-gradient(rgb(var(--surface-border) / 0.45) 1px, transparent 1px)',
                  backgroundSize: `100% ${HOUR_PX}px, 100% ${HOUR_PX / 2}px`,
                }}
              >
                {off.map((item) => (
                  <div
                    key={item.start}
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 bg-surface/60"
                    style={{ top: top(item.start), height: top(item.end) - top(item.start) }}
                  />
                ))}
                {groupBlocks(column.blocks).map((group) => {
                  const describe = (block: PlacedBlock) =>
                    `${minuteLabel(block.start)}–${minuteLabel(block.end)}${
                      column.professionalId ? '' : ` · ${block.resourceDisplayName}`
                    }${block.reason ? ` · ${block.reason}` : ''}`;
                  const box = {
                    top: top(group.start),
                    height: top(group.end) - top(group.start),
                  };
                  return (
                    <Fragment key={group.blocks[0]!.id}>
                      <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-x-0"
                        style={{ ...HATCH, ...box }}
                      />
                      <Tooltip
                        side="right"
                        content={
                          <span className="block space-y-1 tabular-nums">
                            <strong className="block">
                              {group.blocks.length > 1 ? 'Bloqueios' : 'Bloqueio'}
                            </strong>
                            {group.blocks.map((block) => (
                              <span key={block.id} className="block">
                                {describe(block)}
                              </span>
                            ))}
                          </span>
                        }
                      >
                        <button
                          type="button"
                          data-calendar-block
                          aria-label={`${group.blocks.length > 1 ? 'Bloqueios' : 'Bloqueio'}: ${group.blocks.map(describe).join('; ')}`}
                          onClick={(event) => event.stopPropagation()}
                          onMouseMove={(event) => {
                            event.stopPropagation();
                            setGhost(null);
                          }}
                          className="absolute left-px cursor-help rounded-full border border-dashed border-content-faint bg-surface-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
                          style={{ ...HATCH, ...box, width: BLOCK_BAR_PX }}
                        />
                      </Tooltip>
                    </Fragment>
                  );
                })}
                {ghost?.column === column.key && !dragging ? (
                  <div
                    aria-hidden="true"
                    // Por cima dos cards: a prévia é translúcida e o horário dela não pode sumir atrás.
                    className={`pointer-events-none absolute inset-x-[3px] z-10 flex items-start rounded-lg border border-dashed border-brand-500 px-2 pt-[2px] text-[11px] font-semibold leading-none text-brand-400`}
                    style={{
                      top: top(ghost.minute),
                      height: ghost.height,
                      // Quase opaca: o horário da prévia se lê mesmo sobre um card.
                      backgroundColor: 'rgb(var(--surface-card) / 0.92)',
                      backgroundImage:
                        'linear-gradient(rgb(var(--brand-500) / 0.14), rgb(var(--brand-500) / 0.14))',
                    }}
                  >
                    + {minuteLabel(ghost.minute)}
                  </div>
                ) : null}
                {denseGroups.map((group) => {
                  const key = `${column.key}:${group.items.map((item) => item.id).join(':')}`;
                  return (
                    <button
                      key={key}
                      ref={openGroup?.key === key ? groupAnchorRef : undefined}
                      type="button"
                      data-calendar-event
                      aria-label={`${group.items.length} agendamentos entre ${minuteLabel(group.start)} e ${minuteLabel(group.end)}`}
                      aria-expanded={openGroup?.key === key}
                      onClick={(event) => {
                        event.stopPropagation();
                        groupAnchorRef.current = event.currentTarget;
                        setOpenGroup((current) =>
                          current?.key === key ? null : { key, items: group.items },
                        );
                      }}
                      onMouseMove={(event) => {
                        event.stopPropagation();
                        setGhost(null);
                      }}
                      className="absolute z-[5] flex cursor-pointer items-center gap-2 overflow-hidden rounded-xl border border-brand-500/45 bg-surface-card px-2 text-left shadow-card transition hover:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
                      style={{
                        top: top(group.start) + 1,
                        height: Math.max(top(group.end) - top(group.start) - 2, 28),
                        left: inset + 2,
                        width: `calc(100% - ${CREATE_GUTTER_PX + inset + 4}px)`,
                      }}
                    >
                      <span aria-hidden="true" className="flex shrink-0 -space-x-1">
                        {group.items.slice(0, MAX_VISIBLE_LANES).map((item) => (
                          <span
                            key={item.id}
                            className="h-6 w-2 rounded-full border border-surface-card"
                            style={{ backgroundColor: `rgb(${colorOf(item.professionalId)})` }}
                          />
                        ))}
                      </span>
                      <span className="min-w-0">
                        <strong className="block truncate text-xs">
                          {group.items.length} agendamentos
                        </strong>
                        <span className="block truncate text-[10px] tabular-nums text-content-muted">
                          {minuteLabel(group.start)}–{minuteLabel(group.end)} · abrir pasta
                        </span>
                      </span>
                    </button>
                  );
                })}
                {column.items
                  .filter((item) => !groupedIds.has(item.id))
                  .map((item) => {
                    const place = lanes.get(item.id)!;
                    const itemTop = top(item.start);
                    const itemHeight = Math.max(top(item.end) - itemTop - 2, 18);
                    const color = colorOf(item.professionalId);
                    const cancelled = item.status === 'CANCELLED';
                    const completed = item.status === 'COMPLETED';
                    return (
                      <Tooltip
                        key={item.id}
                        content={
                          <span>
                            <strong className="block">{item.clientDisplayName}</strong>
                            <span className="block text-content-muted">
                              {item.serviceDisplayName}
                            </span>
                            {item.overlapWarning ? (
                              <span className="mt-1 block text-warning">{item.overlapWarning}</span>
                            ) : null}
                          </span>
                        }
                      >
                        <button
                          type="button"
                          data-calendar-event
                          onClick={(event) => {
                            event.stopPropagation();
                            if (!drag.justDragged()) onOpen(item, event.currentTarget);
                          }}
                          onPointerDown={(event) => startDrag(event, item)}
                          onMouseMove={(event) => {
                            event.stopPropagation();
                            setGhost(null);
                          }}
                          aria-label={`${item.clientDisplayName}, ${item.serviceDisplayName}, ${minuteLabel(item.start)} às ${minuteLabel(item.end)}, ${professionalLabel(item)}${cancelled ? ', cancelado' : ''}${item.status === 'CONFIRMED' ? ', confirmado' : ''}${completed ? ', concluído' : ''}${item.overlapWarning ? ', ' + item.overlapWarning : ''}`}
                          className={`absolute select-none overflow-hidden rounded-lg border bg-surface-card px-1.5 py-0.5 text-left text-[11.5px] leading-tight transition hover:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 ${
                            // Esmaece só o conteúdo: o fundo segue opaco e não deixa vazar o que está atrás.
                            cancelled
                              ? 'cursor-pointer border-dashed [&>*]:opacity-60'
                              : completed
                                ? 'cursor-pointer [&>*]:opacity-70'
                                : canDrag
                                  ? 'cursor-grab'
                                  : 'cursor-pointer'
                          } ${dragging?.id === item.id ? 'pointer-events-none opacity-30' : ''}`}
                          style={{
                            ...eventStyle(color, cancelled),
                            top: itemTop + 1,
                            height: itemHeight,
                            // A faixa da direita fica livre para clicar e criar mesmo onde há agendamento.
                            left: `calc(${inset}px + (100% - ${CREATE_GUTTER_PX + inset}px) * ${place.lane / place.lanes} + 2px)`,
                            width: `calc((100% - ${CREATE_GUTTER_PX + inset}px) / ${place.lanes} - 4px)`,
                          }}
                        >
                          {/* O horário não repete no card: a posição na grade já o diz, e o detalhe
                          aberto no clique o traz por extenso. */}
                          <span className="flex min-w-0 items-center gap-1">
                            {item.overlapWarning ? (
                              <TriangleAlert
                                aria-hidden="true"
                                className="size-3 shrink-0 text-warning"
                              />
                            ) : null}
                            {item.status === 'CONFIRMED' ? (
                              <CircleCheck
                                aria-hidden="true"
                                className="size-3 shrink-0 text-positive"
                              />
                            ) : completed ? (
                              <Check aria-hidden="true" className="size-3 shrink-0 text-positive" />
                            ) : null}
                            <strong
                              className={`truncate font-semibold ${cancelled ? 'line-through' : ''}`}
                            >
                              {item.clientDisplayName}
                            </strong>
                          </span>
                          {itemHeight > 28 ? (
                            <span className="block truncate text-content-secondary">
                              {item.serviceDisplayName}
                            </span>
                          ) : null}
                          {detailed && itemHeight > 42 ? (
                            <span className="block truncate text-content-secondary">
                              {item.roomDisplayName ? `${roomLabel(item)} · ` : ''}
                              {formatCurrency(item.priceCents, item.currency)}
                            </span>
                          ) : null}
                          {canDrag && !cancelled ? (
                            <span
                              aria-hidden="true"
                              data-calendar-grip="true"
                              className="absolute inset-x-0 bottom-0 h-[7px] cursor-ns-resize"
                            />
                          ) : null}
                        </button>
                      </Tooltip>
                    );
                  })}
                {dropHere ? (
                  <div
                    aria-live="polite"
                    className={`pointer-events-none absolute inset-x-[3px] z-20 rounded-lg border-2 px-2 py-0.5 text-[11px] font-bold ${
                      dragging?.reason
                        ? 'border-danger bg-danger/10 text-danger'
                        : dragging?.warning
                          ? 'text-warning'
                          : 'text-content'
                    }`}
                    style={{
                      top: top(dropHere.start),
                      height: top(dropHere.end) - top(dropHere.start),
                      ...(dragging?.reason
                        ? {}
                        : {
                            borderColor: `rgb(${colorOf(dropHere.professionalId)})`,
                            backgroundColor: `rgb(${colorOf(dropHere.professionalId)} / 0.18)`,
                          }),
                    }}
                  >
                    {minuteLabel(dropHere.start)}–{minuteLabel(dropHere.end)}
                    {dragging?.reason
                      ? ` · ${dragging.reason}`
                      : dragging?.warning
                        ? ` · ${dragging.warning}`
                        : ''}
                  </div>
                ) : null}
                {column.date === today &&
                now.date === today &&
                now.minute > range.start &&
                now.minute < range.end ? (
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 z-10 h-0.5 bg-danger"
                    style={{ top: top(now.minute) }}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
      {openGroup ? (
        <FloatingPanel
          anchorRef={groupAnchorRef}
          onDismiss={() => setOpenGroup(null)}
          role="dialog"
          aria-label="Agendamentos agrupados"
          className="max-h-[min(28rem,calc(100vh-2rem))] w-80 max-w-[calc(100vw-2rem)] rounded-2xl border border-surface-border bg-surface-card p-2 shadow-card"
        >
          <div className="flex items-center justify-between gap-3 px-2 py-2">
            <strong className="text-sm">{openGroup.items.length} agendamentos</strong>
            <button
              type="button"
              onClick={() => setOpenGroup(null)}
              aria-label="Fechar agendamentos agrupados"
              className="grid size-8 cursor-pointer place-items-center rounded-lg text-content-muted transition hover:bg-surface-elevated hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
            >
              <X className="size-4" />
            </button>
          </div>
          <ul className="divide-y divide-surface-border">
            {openGroup.items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpen(item, event.currentTarget);
                  }}
                  className="grid w-full cursor-pointer grid-cols-[3rem_1fr] gap-2 rounded-lg px-2 py-2.5 text-left transition hover:bg-surface-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
                >
                  <span className="text-xs font-semibold tabular-nums">
                    {minuteLabel(item.start)}
                  </span>
                  <span className="min-w-0">
                    <strong className="block truncate text-sm">{item.clientDisplayName}</strong>
                    <span className="block truncate text-xs text-content-muted">
                      {item.serviceDisplayName} · {professionalLabel(item)}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </FloatingPanel>
      ) : null}
    </div>
  );
}

function appointmentGroups(items: PlacedAppointment[]): Array<{
  start: number;
  end: number;
  items: PlacedAppointment[];
}> {
  const sorted = [...items].sort((left, right) => left.start - right.start || left.end - right.end);
  const groups: Array<{ start: number; end: number; items: PlacedAppointment[] }> = [];
  for (const item of sorted) {
    const last = groups.at(-1);
    if (last && item.start < last.end) {
      last.end = Math.max(last.end, item.end);
      last.items.push(item);
    } else {
      groups.push({ start: item.start, end: item.end, items: [item] });
    }
  }
  return groups;
}

function AppointmentDetail({
  appointment,
  capabilities,
  timeZone,
  onChanged,
  onClose,
  onReschedule,
  onCancel,
}: {
  appointment: PlacedAppointment;
  capabilities: SchedulingCapabilities;
  timeZone: string;
  onChanged: (appointment: Appointment) => void;
  onClose: () => void;
  onReschedule: () => void;
  onCancel: () => void;
}) {
  const live = isLive(appointment.status);
  const openClosing = useOpenClosing();
  const openChart = useOpenChart();
  const confirmation = useAppointmentConfirmation(onChanged);
  const busy = confirmation.pending === appointment.id;
  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <AppointmentStatusPill
            status={appointment.status}
            requiresConfirmation={appointment.requiresConfirmation}
          />
          {appointment.overlapWarning ? (
            <p className="flex items-start gap-2 text-sm text-warning">
              <TriangleAlert aria-hidden="true" className="size-4 shrink-0" />
              {appointment.overlapWarning}
            </p>
          ) : null}
          <AppointmentQueueNotice appointment={appointment} timeZone={timeZone} />
          <AppointmentDepositNotice appointment={appointment} timeZone={timeZone} />
          <h3 className="mt-1.5 truncate text-base font-semibold">
            {appointment.clientDisplayName}
          </h3>
        </div>
        <button
          type="button"
          aria-label="Fechar"
          onClick={onClose}
          className="grid size-8 shrink-0 cursor-pointer place-items-center rounded-lg border border-surface-border text-content-muted transition hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
        >
          <X className="size-4" />
        </button>
      </div>
      <dl className="grid grid-cols-[88px_1fr] gap-x-3 gap-y-1.5 text-sm tabular-nums">
        <dt className="text-content-muted">Quando</dt>
        <dd>
          {shortDate(appointment.date)} · {minuteLabel(appointment.start)}–
          {minuteLabel(appointment.end)}
        </dd>
        <dt className="text-content-muted">Serviço</dt>
        <dd>
          {appointment.serviceDisplayName} ·{' '}
          {formatCurrency(appointment.priceCents, appointment.currency)}
        </dd>
        <dt className="text-content-muted">Profissional</dt>
        <dd>{professionalLabel(appointment)}</dd>
        <dt className="text-content-muted">Sala</dt>
        <dd>{roomLabel(appointment)}</dd>
        {appointment.cancellationReason ? (
          <>
            <dt className="text-content-muted">Motivo</dt>
            <dd>{appointment.cancellationReason}</dd>
          </>
        ) : null}
        {live && appointment.confirmedAt ? (
          <>
            <dt className="text-content-muted">Confirmação</dt>
            <dd>Confirmado em {formatDateTime(appointment.confirmedAt, timeZone)}</dd>
          </>
        ) : null}
      </dl>
      {appointment.status === 'CANCELLED' ? (
        <PortalCancellationCard
          appointmentId={appointment.id}
          canDecideRefund={capabilities.canDecidePortalRefund ?? false}
        />
      ) : null}
      <SchedulingCredit
        clientId={appointment.clientId}
        businessUnitId={appointment.businessUnitId}
        prepaid={Boolean(appointment.prepaidBalanceId)}
      />
      {(openClosing || openChart) && appointment.status !== 'CANCELLED' ? (
        <div className="flex flex-wrap gap-2">
          {openClosing ? (
            <Button size="sm" onClick={() => openClosing(appointment.id)}>
              <ClipboardCheck className="size-4" />
              {appointment.status === 'COMPLETED' ? 'Ver atendimento' : 'Abrir atendimento'}
            </Button>
          ) : null}
          {openChart ? (
            <Button size="sm" variant="outline" onClick={() => openChart(appointment.clientId)}>
              <HeartPulse className="size-4" />
              Prontuário
            </Button>
          ) : null}
        </div>
      ) : null}
      {live && capabilities.canUpdate ? (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => void confirmation.confirm(appointment)}
          >
            {appointment.status === 'CONFIRMED' ? (
              <>
                <MessageCircle className="size-4" /> Reenviar confirmação
              </>
            ) : (
              <>
                <CircleCheck className="size-4" /> Confirmar agendamento
              </>
            )}
          </Button>
        </div>
      ) : null}
      {live && (capabilities.canUpdate || capabilities.canCancel) ? (
        <div className="flex flex-wrap gap-2">
          {capabilities.canUpdate ? (
            <Button size="sm" variant="outline" onClick={onReschedule}>
              Remarcar
            </Button>
          ) : null}
          {capabilities.canCancel ? (
            <Button size="sm" variant="outline" className="text-danger" onClick={onCancel}>
              Cancelar agendamento
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-1.5 border-t border-surface-border px-4 py-2.5 text-xs text-content-muted">
      <span className="inline-flex items-center gap-1.5">
        <span
          aria-hidden="true"
          className="h-3 w-4 rounded border"
          style={eventStyle('var(--series-1)', false)}
        />
        Agendado (cor do profissional)
      </span>
      <span className="inline-flex items-center gap-1.5">
        <CircleCheck aria-hidden="true" className="size-3.5 text-positive" />
        Confirmado
      </span>
      <span className="inline-flex items-center gap-1.5">
        <Check aria-hidden="true" className="size-3.5 text-positive" />
        Concluído
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span
          aria-hidden="true"
          className="h-3 w-4 rounded border border-dashed border-content-faint"
        />
        Cancelado
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span
          aria-hidden="true"
          className="h-3 w-4 rounded border border-dashed border-content-faint"
          style={HATCH}
        />
        Bloqueio
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span
          aria-hidden="true"
          className="h-3 w-4 rounded border border-surface-border bg-surface/60"
        />
        Fora do expediente
      </span>
    </div>
  );
}
